/* ------------------------------------------------------------------
 * LoggerPanel.tsx – Developer ▶︎ Logger tab
 * ------------------------------------------------------------------
 * A full-featured log viewer replicating the legacy front end,
 * now implemented in React + TypeScript + Tailwind + React Query.
 */

import React, { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchData } from '../../../../utils/fetch'
import yaml from 'js-yaml'
import clsx from 'classnames'

/* ──────────────────────────────────────────────────────────────────
 *  Type Definitions
 * ──────────────────────────────────────────────────────────────────*/
interface LogRun {
  id_run: number
  id_run_father: number | null
  id_father_service: number | null
  id_service: number
  service_name: string
  timestamp: string
  script_name?: string
  activity_log_title?: string
  status: number
  children?: LogRun[]
}

interface OutcomeItem {
  id_category: number
  id_type: number
  v_string?: string | null
  v_jsonb?: Record<string, any> | null
  v_floatpoint?: number | null
  v_integer?: number | null
}

/* ──────────────────────────────────────────────────────────────────
 *  Helpers
 * ──────────────────────────────────────────────────────────────────*/
const keyFor = (run: LogRun) =>
  `${run.id_run}-${run.id_run_father}-${run.id_father_service}`

const parseLegacyTimestamp = (ts: string): number => {
  // legacy format "YYMMDD HH:mm:ss.cc"
  const [ymd, hms] = ts.split(' ')
  const yy = ymd.slice(0, 2)
  const mm = ymd.slice(2, 4)
  const dd = ymd.slice(4, 6)
  const iso = `20${yy}-${mm}-${dd}T${hms}Z`
  const t = Date.parse(iso)
  return isNaN(t) ? 0 : t
}

const sortRunsDesc = (list: LogRun[]): LogRun[] =>
  [...list].sort((a, b) => parseLegacyTimestamp(b.timestamp) - parseLegacyTimestamp(a.timestamp))

const fmtExecTime = (ms?: number | null) => {
  if (ms == null) return ''
  return ms >= 1000
    ? `\n\nexec time: ${(ms / 1000).toFixed(1)} s`
    : `\n\nexec time: ${ms} ms`
}

/* ──────────────────────────────────────────────────────────────────
 *  RunsTable – left pane
 * ──────────────────────────────────────────────────────────────────*/
interface RunsTableProps {
  runs: LogRun[]
  onRunClick: (run: LogRun) => void
}
const RunsTable: React.FC<RunsTableProps> = ({ runs, onRunClick }) => {
  const [hover, setHover] = useState<string | null>(null)
  const statusText = (s: number) =>
    s >= 200 && s < 400 ? 'OK' : s >= 400 ? 'NOK' : '…'
  const statusClass = (s: number) =>
    s >= 200 && s < 400
      ? 'text-green-400'
      : s >= 400
      ? 'text-rose-400'
      : 'text-amber-400'

  return (
    <table className="w-full text-left">
      <thead>
        <tr className="text-neutral-400 text-xs border-b border-neutral-700">
          <th className="py-1 px-2">Time</th>
          <th className="py-1 px-2">Service</th>
          <th className="py-1 px-2">Script</th>
          <th className="py-1 px-2">Run ID</th>
          <th className="py-1 px-2">Status</th>
        </tr>
      </thead>
      <tbody>
        {runs.map(run => (
          <React.Fragment key={keyFor(run)}>
            {[run, ...(run.children ?? [])].map((r, idx) => {
              const isChild = idx > 0
              const k = keyFor(r)
              const bgClass = hover === k
                ? 'bg-neutral-950'
                : isChild
                ? 'bg-neutral-900'
                : 'bg-neutral-800'
              return (
                <tr
                  key={k}
                  className={clsx(
                    bgClass,
                    'cursor-pointer',
                    isChild ? 'pl-10 font-normal text-sm' : 'font-semibold'
                  )}
                  onMouseEnter={() => setHover(k)}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => onRunClick(r)}
                >
                  <td className="w-1/5 px-2 py-1 whitespace-nowrap">{r.timestamp}</td>
                  <td className="w-1/4 px-2 py-1 truncate">{r.service_name}</td>
                  <td className="w-1/4 px-2 py-1 truncate">{r.activity_log_title ?? r.script_name}</td>
                  <td className="w-1/6 px-2 py-1 font-mono text-xs whitespace-nowrap">{r.id_run}</td>
                  <td className={clsx('w-1/12 px-2 py-1 text-xs font-bold', statusClass(r.status))}>
                    {statusText(r.status)}
                  </td>
                </tr>
              )
            })}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  )
}

/* ──────────────────────────────────────────────────────────────────
 *  OutcomeViewer – right pane
 * ──────────────────────────────────────────────────────────────────*/
interface OutcomeViewerProps {
  outcome: OutcomeItem[] | null
}
const OutcomeViewer: React.FC<OutcomeViewerProps> = ({ outcome }) => {
  if (!outcome) return <p className="text-neutral-400">Select a run.</p>
  if (outcome.length === 0) return <p className="text-neutral-400">No data.</p>

  const display = (item: OutcomeItem): string => {
    if (item.v_jsonb) {
      try { return yaml.dump(item.v_jsonb, { indent: 2, lineWidth: -1 }) }
      catch { return '⚠️ JSON→YAML error' }
    }
    if (item.v_string)      return item.v_string
    if (item.v_floatpoint!=null) return String(item.v_floatpoint)
    if (item.v_integer  !=null) return String(item.v_integer)
    return '∅'
  }

  const inp  = outcome.find(i => i.id_category===0 && i.id_type===1)
  const res  = outcome.find(i => i.id_category===0 && i.id_type===2)
  const exec = outcome.find(i => i.id_category===1 && i.id_type===1)
  const err  = outcome.find(i => i.id_category===2 && i.id_type===0)

  return (
    <div className="space-y-4">
      {inp && (
        <pre className="bg-neutral-950 p-3 rounded text-neutral-200 overflow-auto whitespace-pre-wrap">
          {display(inp)}{fmtExecTime(exec?.v_integer)}
        </pre>
      )}
      {res && (
        <pre className="bg-neutral-950 p-3 rounded text-neutral-100 overflow-auto whitespace-pre-wrap">
          {display(res)}
        </pre>
      )}
      {err && (
        <pre className="bg-rose-950 p-3 rounded text-rose-300 overflow-auto whitespace-pre-wrap">
          {display(err)}
        </pre>
      )}
    </div>
  )
}

/* ──────────────────────────────────────────────────────────────────
 *  LoggerPanel – glue
 * ──────────────────────────────────────────────────────────────────*/
export default function LoggerPanel() {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<LogRun | null>(null)
  const [overlay, setOverlay]   = useState<'green'|'red'|null>(null)

  const runsQ = useQuery<LogRun[], Error>({
    queryKey: ['logger','rootRuns'],
    queryFn: async () => {
      const data = await fetchData(
        '/frontend_react/frontend/get_activity_log_run_list',
        { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ id_runs_fathers_only: true }) }
      )
      return sortRunsDesc(data as LogRun[])
    },
    initialData: [],
  })

  useEffect(() => {
    const proto = window.location.protocol==='https:' ? 'wss':'ws'
    const ws = new WebSocket(`${proto}://${window.location.host}/frontend_react/ws`)
    ws.addEventListener('message', ev => {
      try {
        const payload = JSON.parse(ev.data) as LogRun[]
        if (!Array.isArray(payload)) return
        queryClient.setQueryData<LogRun[]>(['logger','rootRuns'], old => {
          const current = old ?? []
          const map = new Map<string,LogRun>()
          current.forEach(r => map.set(keyFor(r), r))
          payload.forEach(r => map.set(keyFor(r), r))
          return sortRunsDesc(Array.from(map.values()))
        })
      } catch {/*ignore*/}
    })
    return () => ws.close()
  }, [queryClient])

  const outcomeQ = useQuery<OutcomeItem[], Error>({
    queryKey: ['logger','outcome', selected?.id_run],
    queryFn: async () => {
      if (!selected) return []
      const data = await fetchData(
        '/frontend_react/outcomes/get_outcome_run',
        { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ id_service:selected.id_service, id_run:selected.id_run }) }
      )
      const arr = Array.isArray(data) ? data : (data as any).data ?? []
      return arr as OutcomeItem[]
    },
    enabled: !!selected,
  })

  const uploadYaml = useMutation<void,Error,{name:string;content:string}>({
    mutationFn: async ({name,content}:{name:string;content:string}) => {
      const res = await fetch('/script_interpreter/execute_script_stack', {
        method:'POST',
        headers:{
          'Content-Type':'application/x-yaml',
          'X-Script-Name':name,
          Authorization:`Bearer ${localStorage.getItem('jwt')}`,
        },
        body:content,
      })
      if (!res.ok) throw new Error(`${res.status}`)
    },
    onSuccess:() => setOverlay('green'),
    onError:() => setOverlay('red'),
    onSettled:() => setTimeout(()=>setOverlay(null),500),
  })

  const [dragging, setDragging] = useState(false)
  const onDrop = (e:React.DragEvent) => {
    e.preventDefault(); setDragging(false)
    const f = e.dataTransfer.files[0]
    if (!f) return
    const ext = f.name.split('.').pop()?.toLowerCase()
    if (ext==='yaml'||ext==='yml'){
      const reader = new FileReader()
      reader.onload = ev=> uploadYaml.mutate({ name:f.name, content:ev.target?.result as string })
      reader.readAsText(f)
    }
  }

  return (
    <div
      className="relative flex h-[90vh] bg-neutral-900 rounded-md overflow-hidden"
      onDragOver={e=>{e.preventDefault();setDragging(true)}}
      onDragLeave={()=>setDragging(false)}
      onDrop={onDrop}
    >
      {overlay && <div className={clsx('absolute inset-0 pointer-events-none', overlay==='green'?'bg-emerald-700/10':'bg-rose-700/10')} />}

      <aside className="w-1/2 overflow-y-auto border-r border-neutral-800 p-3">
        <h2 className="text-sm text-neutral-300 mb-2">Log Runs</h2>
        {runsQ.isLoading && <p className="text-neutral-500">Loading…</p>}
        {runsQ.isError   && <p className="text-rose-400">{runsQ.error.message}</p>}
        {!runsQ.isLoading && !runsQ.isError && (
          <RunsTable
            runs={runsQ.data}
            onRunClick={r=>{ setSelected(r); outcomeQ.refetch() }}
          />
        )}
      </aside>

      <section className="flex-1 overflow-y-auto p-4">
        {selected ? (
          outcomeQ.isLoading  ? <p className="text-neutral-400">Loading outcome…</p> :
          outcomeQ.isError    ? <p className="text-rose-400">{outcomeQ.error.message}</p> :
          <OutcomeViewer outcome={outcomeQ.data ?? null} />
        ) : <p className="text-neutral-500">Select a run.</p>}
      </section>

      {dragging && (
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10 pointer-events-none">
          <p className="text-lg text-neutral-200">Drop *.yaml* here to execute</p>
        </div>
      )}
    </div>
  )
}
